import { Component, computed, input, output } from '@angular/core';

import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';

export interface MergeDataItem {
  id: number | string;
  primary: string;
  secondary?: string;
}

export interface MergeSelectionState {
  itemsToAdd: Set<number | string>;
  itemsToRemove: Set<number | string>;
}

export interface MergeSelectionChangeEvent {
  type: 'add' | 'remove';
  id: number | string;
  selected: boolean;
}

@Component({
  selector: 'xm-clinical-data-merge-section',
  standalone: true,
  imports: [
    MatCardModule,
    MatCheckboxModule,
    MatIconModule,
    MatButtonModule,
    MatChipsModule
  ],
  template: `
    <mat-card class="merge-section">
      <mat-card-header>
        <mat-icon mat-card-avatar [color]="iconColor()">{{ icon() }}</mat-icon>
        <mat-card-title>{{ title() }}</mat-card-title>
      </mat-card-header>
      <mat-card-content>
        <div class="items-list">
          <!-- Items to Remove (red background) -->
          @for (item of itemsToRemoveList(); track item.id) {
            <div class="item-row remove-row">
              <mat-checkbox
                [checked]="isMarkedForRemoval(item.id)"
                (change)="onExistingItemToggle(item.id, !$event.checked)"
                color="warn">
              </mat-checkbox>
              <div class="item-content">
                <span class="item-primary">{{ item.primary }}</span>
                @if (item.secondary) {
                  <span class="item-secondary">{{ item.secondary }}</span>
                }
              </div>
              <mat-chip class="status-chip remove">REMOVE</mat-chip>
            </div>
          }

          <!-- Existing items (in both) -->
          @for (item of existingMatchItems(); track item.id) {
            <div class="item-row existing-row">
              <mat-checkbox
                [checked]="true"
                [disabled]="true"
                color="primary">
              </mat-checkbox>
              <div class="item-content">
                <span class="item-primary">{{ item.primary }}</span>
                @if (item.secondary) {
                  <span class="item-secondary">{{ item.secondary }}</span>
                }
              </div>
              <mat-chip class="status-chip exists">EXISTS</mat-chip>
            </div>
          }

          <!-- Items to Add (blue background) -->
          @for (item of newReferralItems(); track item.id) {
            <div class="item-row add-row">
              <mat-checkbox
                [checked]="isMarkedForAddition(item.id)"
                (change)="onReferralItemToggle(item.id, $event.checked)"
                color="primary">
              </mat-checkbox>
              <div class="item-content">
                <span class="item-primary">{{ item.primary }}</span>
                @if (item.secondary) {
                  <span class="item-secondary">{{ item.secondary }}</span>
                }
              </div>
              <mat-chip class="status-chip add">ADD</mat-chip>
            </div>
          }

          <!-- Empty state if no items at all -->
          @if (itemsToRemoveList().length === 0 && existingMatchItems().length === 0 && newReferralItems().length === 0) {
            <div class="empty-state">No {{ itemLabel() }} to merge</div>
          }
        </div>
      </mat-card-content>
    </mat-card>
  `,
  styles: [`
    .merge-section {
      margin-bottom: 0;
    }

    .empty-state {
      padding: 24px 16px;
      text-align: center;
      color: var(--mat-color-text-secondary);
      font-style: italic;
    }

    .items-list {
      border: 1px solid var(--mat-color-divider);
      border-radius: 8px;
      overflow: hidden;
    }

    .item-row {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px 16px;
      border-bottom: 1px solid var(--mat-color-divider);
      transition: background-color 0.2s;

      &:last-child {
        border-bottom: none;
      }

      &.remove-row {
        background-color: rgba(var(--mat-color-warn-rgb), 0.1);
      }

      &.existing-row {
        background-color: var(--mat-color-surface-variant);
      }

      &.add-row {
        background-color: rgba(var(--mat-color-primary-rgb), 0.1);
      }
    }

    .item-content {
      display: flex;
      flex-direction: column;
      gap: 2px;
      flex: 1;
      min-width: 0;
    }

    .item-primary {
      font-weight: 500;
    }

    .item-secondary {
      font-size: 0.85em;
      color: var(--mat-color-text-secondary);
    }

    .status-chip {
      font-size: 0.75em;
      min-height: 24px;
      flex-shrink: 0;

      &.exists {
        background-color: var(--mat-color-surface-variant);
        font-style: italic;
      }

      &.remove {
        background-color: var(--mat-color-warn);
        color: white;
      }

      &.add {
        background-color: var(--mat-color-primary);
        color: white;
      }
    }
  `]
})
export class ClinicalDataMergeSectionComponent {
  // Display configuration
  title = input.required<string>();
  icon = input<string>('list');
  iconColor = input<'primary' | 'accent' | 'warn'>('primary');
  itemLabel = input<string>('items');

  // Data
  existingItems = input<MergeDataItem[]>([]);
  referralItems = input<MergeDataItem[]>([]);
  existingItemIds = input<Set<number | string>>(new Set());

  // Selection state
  itemsToAdd = input<Set<number | string>>(new Set());
  itemsToRemove = input<Set<number | string>>(new Set());

  // Outputs
  selectionChange = output<MergeSelectionChangeEvent>();

  // Computed: Items that are in our patient but NOT in referral (candidates for removal)
  itemsToRemoveList = computed<MergeDataItem[]>(() => {
    const referralItemNames = new Set(
      this.referralItems().map(item => item.primary.toLowerCase().trim())
    );
    return this.existingItems().filter(
      item => !referralItemNames.has(item.primary.toLowerCase().trim())
    );
  });

  // Computed: Items that exist in both patient and referral
  existingMatchItems = computed<MergeDataItem[]>(() => {
    return this.referralItems().filter(item => this.existingItemIds().has(item.id));
  });

  // Computed: New items from referral (not in existing patient)
  newReferralItems = computed<MergeDataItem[]>(() => {
    return this.referralItems().filter(item => !this.existingItemIds().has(item.id));
  });

  // Check if referral item already exists in patient record
  isExistingItem(id: number | string): boolean {
    return this.existingItemIds().has(id);
  }

  // Check if item is marked for removal
  isMarkedForRemoval(id: number | string): boolean {
    return this.itemsToRemove().has(id);
  }

  // Check if item is marked for addition
  isMarkedForAddition(id: number | string): boolean {
    return this.itemsToAdd().has(id);
  }

  // Toggle existing item (unchecked = remove)
  onExistingItemToggle(id: number | string, checked: boolean): void {
    // If unchecked, mark for removal; if checked, remove from removal list
    this.selectionChange.emit({
      type: 'remove',
      id,
      selected: !checked // selected=true means "mark for removal"
    });
  }

  // Toggle referral item (checked = add)
  onReferralItemToggle(id: number | string, checked: boolean): void {
    this.selectionChange.emit({
      type: 'add',
      id,
      selected: checked
    });
  }
}
